// ============================================================================
// QuickActions — the big tappable home actions row (Wave 4).
// ----------------------------------------------------------------------------
// Three Pulse "pulse" buttons, mobile-first (a 3-col grid that stays usable at
// 390px): Log meal · Log water · Log exercise.
//   • Log meal / Log exercise deep-link to the existing /me/log flow with the
//     right tab (?tab=meal | ?tab=exercise) — reusing the Wave 1-2 log screens.
//   • Log water adds 250 ml INLINE (one tap, no navigation) via the existing
//     useAddClientWater mutation; the rings/feed refresh from the invalidated
//     queries. A long-press alternative isn't needed — the dashboard already
//     shows the ocean ring, and the full water tracker lives on /me/log.
//   • Message deep-links to the Wave-5 /me/messages thread (the action was
//     deferred from Wave 4; wired here now). Coral to match the messaging accent.
//
// Colour-coded by provenance to match the calendar legend: meal amber, water
// ocean, exercise lime, message coral. All copy via i18n keys; reduced-motion
// friendly (tap feedback is a CSS active scale, which collapses under RM).
// ============================================================================
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Banner } from "../../components";
import { useAddClientWater, todayDay } from "../hooks";
import { CLIENT_LOG_PATH, CLIENT_MESSAGES_PATH } from "../routes";
import { cn } from "../../lib/cn";

type Accent = "amber" | "ocean" | "lime" | "coral";

const ACCENT: Record<Accent, string> = {
  amber: "bg-amber-soft text-amber-600 ring-amber/15 hover:bg-amber/15",
  ocean: "bg-ocean-soft text-ocean-600 ring-ocean/15 hover:bg-ocean/15",
  lime: "bg-lime-soft text-lime-600 ring-lime/15 hover:bg-lime/15",
  coral: "bg-coral-soft text-coral-600 ring-coral/15 hover:bg-coral/15",
};

const TILE =
  "flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl px-2 py-4 " +
  "font-display text-sm font-bold ring-1 ring-inset transition-all duration-fast ease-kinetic " +
  "active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2";

function MealIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" aria-hidden>
      <path d="M5 3v8m0 0a2 2 0 0 0 2-2V3M3 3v6a2 2 0 0 0 2 2m0 0v10M16 3c-1.7 0-3 2-3 5s1 4 3 4m0 0v9m0-9c1.7 0 3-1 3-4s-1.3-5-3-5"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function WaterIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" aria-hidden>
      <path d="M12 3.5c3.6 4.2 6 7.4 6 10.5a6 6 0 0 1-12 0c0-3.1 2.4-6.3 6-10.5z"
        stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}
function DumbbellIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" aria-hidden>
      <path d="M6.5 6.5l11 11M4 9l-1.5 1.5a2 2 0 0 0 0 2.8L4 15M9 4l-1.5 1.5M20 9l1.5 1.5a2 2 0 0 1 0 2.8L20 15M15 20l1.5-1.5"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function MessageIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" aria-hidden>
      <path d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
        stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

export function QuickActions() {
  const { t } = useTranslation("clientPortal");
  const { t: tm } = useTranslation("messages");
  const addWater = useAddClientWater();
  const [justAdded, setJustAdded] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const logWater = () =>
    addWater.mutate(
      { day: todayDay(), delta_ml: 250 },
      {
        onSuccess: () => {
          // Transient "Added!" cue that reverts to "+250 ml" to invite a re-tap.
          window.clearTimeout(timer.current);
          setJustAdded(true);
          timer.current = window.setTimeout(() => setJustAdded(false), 1500);
        },
      },
    );

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2.5">
        <Link to={`${CLIENT_LOG_PATH}?tab=meal`} className={cn(TILE, ACCENT.amber)}>
          <MealIcon />
          <span>{t("quick.meal")}</span>
        </Link>

        <button
          type="button"
          onClick={logWater}
          disabled={addWater.isPending}
          aria-label={t("quick.waterAria")}
          className={cn(TILE, ACCENT.ocean, "disabled:opacity-60")}
        >
          <WaterIcon />
          <span>{t("quick.water")}</span>
          <span className="text-xs font-extrabold opacity-80">
            {justAdded ? t("quick.waterAdded") : t("quick.waterDelta")}
          </span>
        </button>

        <Link to={`${CLIENT_LOG_PATH}?tab=exercise`} className={cn(TILE, ACCENT.lime)}>
          <DumbbellIcon />
          <span>{t("quick.exercise")}</span>
        </Link>

        {/* Wave-5 Message action (deferred from Wave 4) — opens the thread. */}
        <Link
          to={CLIENT_MESSAGES_PATH}
          aria-label={tm("quickAction.hint")}
          className={cn(TILE, ACCENT.coral)}
        >
          <MessageIcon />
          <span>{tm("quickAction.label")}</span>
        </Link>
      </div>

      {addWater.isError && (
        <Banner tone="danger" title={t("quick.waterError")} />
      )}
    </div>
  );
}
